/**
 * ProductFormV2 - Enhanced product form custom element
 *
 * Manages a complex product form with:
 * - Flavor selection
 * - Purchase type switching (subscription vs one-time)
 * - Subscription plan selection
 * - Variant management
 * - Addon selection
 * - Dynamic pricing updates
 * - Form state management
 *
 */
class ProductFormV2 {
	constructor() {
		this.form = document.querySelector(".product-form-v2")
		this.productData = null
		this.currentSelections = null
		this.elements = null

		// Initialize lastMonthSupplyByType early to prevent undefined errors
		this.lastMonthSupplyByType = { onetime: null, subscribe: null }

		// Initialize pending selection restore data
		this.pendingSelectionRestore = null

		// Store event handler for cleanup
		this.productData = this.buildProductData()
		this.currentSelections = this.getInitialSelections()
		this.elements = this.getElements()
		this.init()
	}

	buildProductData() {
		if (!this.form) return { flavors: [], variants: [], subscriptionPlans: [] }

		if (window.productFormV2?.variants) {
			const variants = window.productFormV2.variants
			const flavors = this.extractFlavorsFromVariants(variants)
			const subscriptionPlans = this.buildSubscriptionPlans()
			return { flavors, variants, subscriptionPlans }
		}

		const flavors = []
		const variants = []

		this.form.querySelectorAll(".flavor-option").forEach((option) => {
			const flavor = option.dataset.flavor
			const available = option.dataset.available === "true"
			flavors.push({ name: flavor, available })
		})

		this.form.querySelectorAll(".variant-option").forEach((option) => {
			const variantId = option.dataset.variantId
			const flavor = option.dataset.variantFlavor
			const addon = option.dataset.variantAddon
			const price = parseFloat(option.dataset.variantPrice)
			const comparePrice = parseFloat(option.dataset.variantComparePrice)
			const available = option.dataset.variantAvailable === "true"

			variants.push({
				id: variantId,
				flavor,
				addon,
				price,
				comparePrice,
				available
			})
		})

		const subscriptionPlansBuilt = this.buildSubscriptionPlans()

		return { flavors, variants, subscriptionPlans: subscriptionPlansBuilt }
	}

	extractFlavorsFromVariants(variants) {
		const flavors = []
		const flavorSet = new Set()

		variants.forEach((variant) => {
			const flavor = variant.option1
			if (flavor && !flavorSet.has(flavor)) {
				flavorSet.add(flavor)
				flavors.push({
					name: flavor,
					available: variant.available
				})
			}
		})

		return flavors
	}

	buildSubscriptionPlans() {
		const subscriptionPlans = []

		this.form.querySelectorAll(".plan-option[data-plan-id]").forEach((option) => {
			const planId = option.dataset.planId
			const planName = option.dataset.planName
			const months = parseInt(option.dataset.months) || 1
			const price = parseFloat(option.dataset.price) || 0
			const total = parseFloat(option.dataset.total) || 0
			const originalPrice = parseFloat(option.dataset.originalPrice) || 0
			const discount = parseInt(option.dataset.discount) || 0

			subscriptionPlans.push({
				id: planId,
				name: planName,
				months,
				price,
				total,
				originalPrice,
				discount
			})
		})

		return subscriptionPlans
	}

	getInitialSelections() {
		if (!this.form || !this.productData) {
			return {
				flavor: null,
				variant: null,
				subscriptionPlan: null,
				purchaseType: "onetime",
				addon: "",
				monthSupply: null
			}
		}

		const selectedFlavor =
			this.form.querySelector(".flavor-option.selected")?.dataset.flavor ||
			this.productData.flavors[0]?.name

		const addonSelect = this.form.querySelector(".addon-select")
		const selectedAddon = addonSelect ? addonSelect.value : ""
		const selRadio = document.querySelector(
			'.subscription-plans input[name="selling_plan_ui"]:checked'
		)
		const sel = selRadio ? selRadio.getAttribute("option2") : null

		// Find variants that match the selected flavor and addon
		let availableVariants = this.productData.variants.filter(
			(v) => v.option1 === selectedFlavor && v.option3 === selectedAddon && v.available
		)

		// If no variants match the addon, try without addon filter
		if (availableVariants.length === 0) {
			availableVariants = this.productData.variants.filter(
				(v) => v.option1 === selectedFlavor && v.available && (sel ? v.option2 === sel : true)
			)
		}

		// If still no variants, just use any available variant
		if (availableVariants.length === 0) {
			availableVariants = this.productData.variants.filter((v) => v.available)
		}

		let selectedVariant = availableVariants[0] || this.productData.variants[0]
		const selectedPlan = this.productData.subscriptionPlans[0]

		// Determine initial month supply
		let initialMonthSupply = sel || null

		// Override with flag default (e.g. 3 Month Supply when A/B flag is on)
		if (typeof window.cnDefaultMonthSupply === 'string') {
			initialMonthSupply = window.cnDefaultMonthSupply
			// Also pick the matching variant so currentSelections.variant is correct from the start
			const defaultVariant = availableVariants.find(v => v.option2 === window.cnDefaultMonthSupply)
			if (defaultVariant) {
				selectedVariant = defaultVariant
			}
		}

		// Check if the initial combination is available
		if (initialMonthSupply && selectedFlavor) {
			const variantExists = this.productData.variants.find(
				(v) => v.option1 === selectedFlavor &&
				       v.option3 === selectedAddon &&
				       v.option2 === initialMonthSupply &&
				       v.available
			)

			if (!variantExists) {
				const allSupplies = [...new Set(this.productData.variants.map(v => v.option2))]
					.sort((a, b) => parseInt(a) - parseInt(b))
				const currentIndex = allSupplies.indexOf(initialMonthSupply)
				const less = currentIndex > -1 ? allSupplies.slice(0, currentIndex).reverse() : []
				const more = currentIndex > -1 ? allSupplies.slice(currentIndex + 1) : allSupplies
				const supplyOrder = [initialMonthSupply, ...less, ...more]

				const addonOrder = [...new Set([selectedAddon, 'Mini-Capsule Omegas', 'No Omegas', 'Liquid Omegas'])]

				let foundAlternative = false
				outer: for (const supply of supplyOrder) {
					if (this.isSupplyHidden(selectedFlavor, supply)) {
						continue
					}
					for (const addon of addonOrder) {
						const altVariant = this.productData.variants.find(
							(v) => v.option1 === selectedFlavor &&
							       v.option3 === addon &&
							       v.option2 === supply &&
							       v.available
						)
						if (altVariant) {
							initialMonthSupply = supply
							selectedAddon = addon
							foundAlternative = true
							break outer
						}
					}
				}
			}
		}


		return {
			flavor: selectedFlavor,
			variant: selectedVariant,
			subscriptionPlan: selectedPlan,
			purchaseType: "onetime",
			addon: selectedAddon,
			monthSupply: initialMonthSupply
		}
	}

	getElements() {
		const elements = {
			flavorInputs: this.form.querySelectorAll('.flavor-option input[type="radio"]'),
			purchaseTypeTabs: this.form.querySelectorAll(".tab-button"),
			subscriptionPlans: this.form.querySelector(".subscription-plans"),
			onetimeOptions: this.form.querySelector(".onetime-options"),
			planInputs: this.form.querySelectorAll('.plan-option input[type="radio"]'),
			globalSellingPlanSelect: this.form.querySelector('input[name="selling_plan"]'),
			variantInputs: this.form.querySelectorAll('input[name="variant_id"]'),
			addonSelect: this.form.querySelector(".addon-select"),
			totalPricing: this.form.querySelector(".total-pricing"),
			originalTotal: this.form.querySelector(".original-total"),
			finalTotal: this.form.querySelector(".final-total"),
			// Try multiple selectors for hidden fields
			hiddenVariantId: this.form.querySelector('.form-variant-id[type="hidden"], input[name="id"][type="hidden"], input[name="variant_id"][type="hidden"]'),
			hiddenFlavor: this.form.querySelector('.form-selected-flavor[type="hidden"], input[name="flavor"][type="hidden"]'),
			hiddenSellingPlan: this.form.querySelector('.form-selling-plan[type="hidden"], input[name="selling_plan"][type="hidden"]'),
			hiddenAddon: this.form.querySelector('.form-addon-omega3[type="hidden"], input[name="addon"][type="hidden"]'),
			// Helpers to find currently checked radios
			getCheckedPlanRadio: () => this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked'),
			getCheckedOnetimeVariantRadio: () => this.form.querySelector('.onetime-options input[name="variant_id"]:checked')
		}
		return elements
	}

	init() {
		// Ensure hidden form fields exist FIRST
		const load = document.querySelectorAll('.product-form-v2-option.load')
		load.forEach((option) => {
			option.classList.remove('load')
		})
		this.ensureHiddenFieldsExist()
		this.setupEventListeners()
		this.updateDisplay()
		this.updatePricing()
		this.updateFormFields()
		this.updateAddToCartButton()
		
		this.syncDropdown2WithAddonSelect(this.currentSelections.addon)
		this.addToCartHandler = (e) => {
			e.preventDefault()
			this.handleAddToCart()
		}
		this.form.querySelector(".add-to-cart-button").addEventListener('click', this.addToCartHandler)
	}

	setupEventListeners() {
		if (!this.elements) {
			return
		}

		// Flavor selection
		this.elements.flavorInputs.forEach((input) => {
			input.addEventListener("change", (e) => {
				try {
					const selectedFlavor = e.target.value
					this.updateFlavor(selectedFlavor)
				} catch (error) {
					console.error("Error handling flavor change:", error)
				}
			})
		})

		// Purchase type tabs
		this.elements.purchaseTypeTabs.forEach((tab) => {
			tab.addEventListener("click", (e) => {
				try {
					e.preventDefault()
					const purchaseType = tab.dataset.purchaseType
					this.updatePurchaseType(purchaseType)
				} catch (error) {
					console.error("Error handling purchase type change:", error)
				}
			})
		})

		// Subscription plan selection
		this.elements.planInputs.forEach((input) => {
			input.addEventListener("change", () => {
				const planIdWithIndex = input.value
				const resolvedPlanId = planIdWithIndex.toString().split("_")[0]
				const monthSupply = input.getAttribute("option2")
				
				// Update month supply selection
				this.currentSelections.monthSupply = monthSupply
				// Remember last selection for subscribe
				if (this.lastMonthSupplyByType) {
					this.lastMonthSupplyByType.subscribe = monthSupply
				}
				
				// set hidden selling_plan for native form submission
				if (this.elements.globalSellingPlanSelect) {
					this.elements.globalSellingPlanSelect.value = resolvedPlanId
				}
				// ensure radio reflects selection visually
				input.checked = true
				input.setAttribute("checked", "checked")
				
				// Ensure only one plan option is selected by deselecting others
				this.deselectOtherPlanOptions(resolvedPlanId)
				
				// update selected subscription plan and the variant behind this plan
				const planVariantId = input.dataset.variantId
				if (planVariantId) {
					this.updateVariant(planVariantId)
				}
				this.updateSubscriptionPlan(resolvedPlanId)
				
				// This will automatically adjust flavor if needed for the new month supply
				this.updateVariantForCurrentSelections()
				this.updateDisplay()
				this.updateVariantVisualSelection()
				this.updatePricing()
				this.updateFormFields()
				this.updateAddToCartButton()
			})
		})

		// Variant selection for one-time
		this.elements.variantInputs.forEach((input) => {
			input.addEventListener("change", (e) => {
				try {
					const variantId = e.target.value
					const variantOption = input.closest('.variant-option')
					const monthSupply = variantOption?.dataset?.monthSupply || null
					

					// Update month supply selection
					this.currentSelections.monthSupply = monthSupply
					// Remember last selection for onetime
					if (this.lastMonthSupplyByType) {
						this.lastMonthSupplyByType.onetime = monthSupply
					}

					// Ensure only one variant option is selected by deselecting others with same flavor and addon
					this.deselectOtherVariantOptions(variantId)

					this.updateVariant(variantId)
					
					// This will automatically adjust flavor if needed for the new month supply
					this.updateVariantForCurrentSelections()
					this.updateDisplay()
					this.updateVariantVisualSelection()
					this.updatePricing()
					this.updateFormFields()
					this.updateAddToCartButton()
				} catch (error) {
					console.error("Error handling variant change:", error)
				}
			})
		})

		// Addon selection
		if (this.elements.addonSelect) {
			this.elements.addonSelect.addEventListener("change", (e) => {
				try {
					const addonValue = e.target.value
					this.updateAddon(addonValue)
					this.updateDisplay()
					this.updateVariantVisualSelection()
					this.updatePricing()
					this.updateFormFields()
					
					// Sync with dropdown2
					this.syncDropdown2WithAddonSelect(addonValue)
				} catch (error) {
					console.error("Error handling addon change:", error)
				}
			})
			
			// Add click event to open dropdown2 when addon-select is clicked
			this.elements.addonSelect.addEventListener("click", (e) => {
				this.openDropdown2()
			})
		}

		// "Switch to subscribe & save" button
		const subscribeSwitch = document.getElementById('subscribe-switch')
		if (subscribeSwitch) {
			subscribeSwitch.addEventListener('click', () => {
				this.updatePurchaseType('subscribe')
			})
		}

		// Setup dropdown2 functionality
		this.setupDropdown2()

		// Handle direct clicks on variant option elements to ensure proper selection
		this.setupVariantOptionClickHandlers()
		
		// Add event delegation for month supply and flavor changes to update peach descriptor immediately
		this.form.addEventListener('change', (e) => {
			// Check if the change is on a month supply radio button (subscription or one-time) or flavor
			if (e.target.matches('input[name="selling_plan_ui"], input[name="variant_id"], input[name="flavor"]')) {
				// Small delay to ensure DOM is updated
				setTimeout(() => {
					this.updatePeachDescriptor()
				}, 0)
			} else {
			}
		})
	}

	setupVariantOptionClickHandlers() {
		// Handle direct clicks on variant option elements
		this.form.querySelectorAll('.variant-option').forEach((option) => {
			option.addEventListener('click', (e) => {
				// Don't handle if clicking on the radio button itself (that's handled by the change event)
				if (e.target.type === 'radio') return
				
				const variantId = option.dataset.variantId
				if (!variantId) return
				
				// Find and check the radio button
				const radio = option.querySelector('input[type="radio"]')
				if (radio) {
					radio.checked = true
					radio.setAttribute('checked', 'checked')
					// Trigger the change event to ensure all our logic runs
					radio.dispatchEvent(new Event('change', { bubbles: true }))
				}
			})
		})

		// Handle direct clicks on plan option elements
		this.form.querySelectorAll('.plan-option').forEach((option) => {
			option.addEventListener('click', (e) => {
				const planId = option.dataset.planId
				if (!planId) return
				
				// Find and check the radio button
				const radio = option.querySelector('input[type="radio"]')
				
				if (radio) {
					radio.checked = true
					radio.setAttribute('checked', 'checked')
					// Trigger the change event to ensure all our logic runs
					radio.dispatchEvent(new Event('change', { bubbles: true }))
				}
			})
		})
	}

	// Handle section errors
	handleSectionError(errorMessage) {
		console.error("Section rendering error:", errorMessage)

		// Show user-friendly error message
		const container = document.querySelector(".product-form-enhanced")
		if (container) {
			const errorDiv = document.createElement("div")
			errorDiv.className = "section-error"
			errorDiv.innerHTML = `
				<p>Sorry, there was an error loading this content. Please refresh the page.</p>
				<button onclick="location.reload()">Refresh Page</button>
			`

			// Clear existing content and show error
			container.innerHTML = ""
			container.appendChild(errorDiv)
		}
	}

	initializeTabs() {
		// Re-setup tab event listeners
		const tabs = this.querySelectorAll(".tab-button")
		tabs.forEach((tab) => {
			tab.addEventListener("click", (e) => {
				e.preventDefault()
				const purchaseType = tab.dataset.purchaseType
				this.updatePurchaseType(purchaseType)
			})
		})
	}

	validateFormForSubmission() {
		
		// Validate for Shopify cart submission
		if (!this.currentSelections?.variant?.id) {
			console.error("❌ No variant selected - cannot add to cart")
			return false
		}

		if (!this.currentSelections?.flavor) {
			console.error("❌ No flavor selected")
			return false
		}

		// Ensure hidden inputs are properly set
		if (!this.elements?.hiddenVariantId?.value) {
			console.error("❌ Hidden variant ID not set - cart submission will fail")
			return false
		}

		// For subscriptions, ensure selling plan is set
		if (this.currentSelections.purchaseType === "subscribe") {
			if (!this.currentSelections?.subscriptionPlan?.id) {
				console.error("❌ No subscription plan selected for subscription")
				return false
			}

			if (!this.elements?.hiddenSellingPlan?.value) {
				console.error("❌ Hidden selling plan not set for subscription")
				return false
			}
		}

		return true
	}

	validateForm() {
		return this.validateFormForSubmission()
	}

	updateFlavor(flavor) {
		// Preserve current selections
		const preservedSelections = {
			addon: this.currentSelections.addon,
			monthSupply: this.currentSelections.monthSupply,
			purchaseType: this.currentSelections.purchaseType
		}
		

		// Set up pending selection restore BEFORE clearing selections
		this.setPendingSelectionRestore()

		// Update user's preferred flavor when they manually select
		this.userPreferredFlavor = flavor

		this.currentSelections.flavor = flavor
		this.updateFlavorVisualSelection(flavor)

		// Clear variant and subscription plan selections when changing flavor
		this.currentSelections.variant = null
		this.currentSelections.subscriptionPlan = null

		// Deselect all variant and plan options
		this.form.querySelectorAll('.variant-option, .plan-option').forEach((option) => {
			const radio = option.querySelector('input[type="radio"]')
			if (radio) {
				radio.checked = false
				radio.removeAttribute('checked')
			}
			option.classList.remove('selected')
		})

		// Restore preserved selections
		this.currentSelections.addon = preservedSelections?.addon
		this.currentSelections.monthSupply = preservedSelections?.monthSupply
		this.currentSelections.purchaseType = preservedSelections?.purchaseType

		// Check if the preserved combination is available in the new flavor
		const desiredVariant = this.findAvailableVariant(
			flavor,
			preservedSelections.addon,
			preservedSelections.monthSupply,
			preservedSelections.purchaseType
		)

		if (!desiredVariant || this.isSupplyHidden(flavor, preservedSelections.monthSupply)) {
			// Build supply order: same first, then less (descending), then more (ascending)
			const supplyOrder = this.getSupplyFallbackOrder(preservedSelections.monthSupply)

			// Addon fallback priority: same → Mini-Capsule Omegas → No Omegas → Liquid Omegas
			const addonOrder = [...new Set([
				preservedSelections.addon,
				'Mini-Capsule Omegas',
				'No Omegas',
				'Liquid Omegas'
			])]

			let foundAlternative = false
			outer: for (const supply of supplyOrder) {
				if (this.isSupplyHidden(flavor, supply)) {
					continue
				}
				for (const addon of addonOrder) {
					const variant = this.findAvailableVariant(flavor, addon, supply, preservedSelections.purchaseType)
					if (variant) {
						this.currentSelections.monthSupply = supply
						this.currentSelections.addon = addon
						foundAlternative = true
						break outer
					}
				}
			}

			if (!foundAlternative) {
			}
		} else {
		}

		// Update addon options based on new flavor
		this.updateAddonOptionsForFlavor(flavor)

		this.updateVariantForCurrentSelections()
		this.updateDisplay()

		// Universal restore will happen automatically in updateDisplay()

		this.updatePricing()
		this.updateFormFields()
		this.updateAddToCartButton()
		this.syncOptionsCorrector()

	}
	syncOptionsCorrector() {

		const optts_onetime = document.querySelectorAll('.onetime-options .product-form-v2-option:not(.hidden)')
		const optts_subscribe = document.querySelectorAll('.subscription-plans .product-form-v2-option:not(.hidden)')
		if (this.currentSelections.purchaseType === 'subscribe') {
			optts_subscribe.forEach((option) => {
				
				if (option.dataset.variantFlavor === this.currentSelections.flavor && option.dataset.variantAddon === this.currentSelections.addon && option.dataset.monthSupply === this.currentSelections.monthSupply) {
					option.classList.add('selected')
				} else {
					option.classList.remove('selected')
				}
			})
		} else {
			optts_onetime.forEach((option) => {
				
				if (option.dataset.variantFlavor === this.currentSelections.flavor && option.dataset.variantAddon === this.currentSelections.addon && option.dataset.monthSupply === this.currentSelections.monthSupply) {
					option.classList.add('selected')
				} else {
					option.classList.remove('selected')
				}
			})
		}
	}
	// Auto-select first visible subscription plan if none are selected
	autoSelectFirstVisibleSubscriptionPlan() {
		if (!this.elements.subscriptionPlans) return

		// Check if subscription plans are visible (not hidden)
		if (this.elements.subscriptionPlans.classList.contains("hidden")) {
			return // Don't auto-select if subscription plans are hidden
		}

		// Find all visible (not hidden) plan radios
		const allPlanRadios = Array.from(
			this.elements.subscriptionPlans.querySelectorAll(
				'input[type="radio"][name="selling_plan_ui"]'
			)
		).filter(radio => !radio.closest(".plan-option")?.classList.contains("hidden"))

		if (allPlanRadios.length === 0) {
			return // No visible plans available
		}

		// Check if any visible plan is already selected
		const checkedVisiblePlan = allPlanRadios.find(radio => radio.checked)
		if (checkedVisiblePlan) {
			// Simulate the plan selection process
			const planOption = checkedVisiblePlan.closest('.plan-option')
			if (planOption) {
				planOption.classList.add("selected")
			}
			// Trigger the change event to ensure all logic runs
			// checkedVisiblePlan.dispatchEvent(new Event('change', { bubbles: true }))
		}
	}

	// New method to update addon options based on selected flavor
	updateAddonOptionsForFlavor(flavor) {
		if (!this.elements.addonSelect) return
		
		// Get all addon options
		const addonOptions = this.elements.addonSelect.querySelectorAll('option')
		const currentAddon = this.currentSelections.addon
		
		// Check which addons are available for this flavor
		const availableAddons = new Set()
		this.productData.variants.forEach(variant => {
			if (variant.option1 === flavor && variant.available) {
				if (variant.option3) {
					availableAddons.add(variant.option3)
				}
			}
		})
		
		
		// Update addon select options
		addonOptions.forEach(option => {
			const addonValue = option.value
			const adminAvailable = option.dataset.adminAvailable !== 'false'
			if (availableAddons.has(addonValue) && adminAvailable) {
				option.disabled = false
				option.style.display = ''
			} else {
				option.disabled = true
				option.style.display = 'none'
			}
		})

		// If current addon is not available for this flavor, select the first available one
		if (currentAddon && !availableAddons.has(currentAddon)) {
			const firstAvailableAddon = Array.from(availableAddons).find(addon => {
				const opt = this.elements.addonSelect.querySelector(`option[value="${addon}"]`)
				return opt?.dataset.adminAvailable !== 'false'
			})
			if (firstAvailableAddon) {
				this.currentSelections.addon = firstAvailableAddon
				this.elements.addonSelect.value = firstAvailableAddon
			}
		}
		
	}

	updateAddToCartButton() {
		const addToCartButton = this.form.querySelector('.add-to-cart-button')
		if (!addToCartButton) return
		const text = addToCartButton.querySelector('.add-to-cart-text')
		if (!text) return
		text.innerHTML = "ADD TO CART"

		addToCartButton.style.opacity = "1"
		addToCartButton.disabled = false
		setTimeout(() => {
			// Only disable if the selected flavor is not available
		const selectedFlavorOption = this.form.querySelector(`.flavor-option[data-flavor="${this.currentSelections.flavor}"]`)
		const isFlavorAvailable = selectedFlavorOption?.dataset.available === "true"
		const selectedOption = this.form.querySelector(`.product-form-v2-option.selected:not(.hidden)`)
		const isOptionAvailable = selectedOption?.dataset.available !== "false"
		


		if (!isFlavorAvailable) {
			addToCartButton.style.opacity = "0.5"
			addToCartButton.disabled = true
			text.innerHTML = "Out of Stock"
		}
		if (!isOptionAvailable) {
			addToCartButton.style.opacity = "0.5"
			addToCartButton.disabled = true
			text.innerHTML = "Out of Stock"
		}
	
	}, 100)
	}

	ensureHiddenFieldsExist() {
		
		// Create hidden variant ID field if it doesn't exist
		if (!this.elements.hiddenVariantId) {
			const hiddenVariantId = document.createElement('input')
			hiddenVariantId.type = 'hidden'
			hiddenVariantId.name = 'id'
			hiddenVariantId.className = 'form-variant-id'
			this.form.appendChild(hiddenVariantId)
			this.elements.hiddenVariantId = hiddenVariantId
		} else {
		}

		// Create hidden flavor field if it doesn't exist
		if (!this.elements.hiddenFlavor) {
			const hiddenFlavor = document.createElement('input')
			hiddenFlavor.type = 'hidden'
			hiddenFlavor.name = 'flavor'
			hiddenFlavor.className = 'form-selected-flavor'
			this.form.appendChild(hiddenFlavor)
			this.elements.hiddenFlavor = hiddenFlavor
		}

		// Create hidden selling plan field if it doesn't exist
		if (!this.elements.hiddenSellingPlan) {
			const hiddenSellingPlan = document.createElement('input')
			hiddenSellingPlan.type = 'hidden'
			hiddenSellingPlan.name = 'selling_plan'
			hiddenSellingPlan.className = 'form-selling-plan'
			this.form.appendChild(hiddenSellingPlan)
			this.elements.hiddenSellingPlan = hiddenSellingPlan
		}

		// Create hidden addon field if it doesn't exist
		if (!this.elements.hiddenAddon) {
			const hiddenAddon = document.createElement('input')
			hiddenAddon.type = 'hidden'
			hiddenAddon.name = 'addon'
			hiddenAddon.className = 'form-addon-omega3'
			this.form.appendChild(hiddenAddon)
			this.elements.hiddenAddon = hiddenAddon
		}
	}

	handleAddToCart() {
		try {
			// Debug current selections
			
			// Validate form before submission
			if (!this.validateFormForSubmission()) {
				console.error("❌ Form validation failed - cannot add to cart")
				return
			}

			// Get the add to cart button
			const addToCartButton = this.form.querySelector('.add-to-cart-button')
			if (!addToCartButton) return
			const buttonText = addToCartButton.querySelector('.add-to-cart-text')
			if (!buttonText) return
			// Disable button and show loading state
			addToCartButton.disabled = true
			addToCartButton.style.opacity = "0.5"
			buttonText.innerHTML = "Adding to Cart..."

			// Prepare form data for submission
			const formData = new FormData(this.form)
			
			// Add any missing required fields (will re-normalize below by purchase type)
			if (!formData.get('id') && this.currentSelections.variant?.id) {
				formData.set('id', this.currentSelections.variant.id)
			}
			
			// Normalize by purchase type
			if (this.currentSelections.purchaseType === 'subscribe') {
				// Ensure variant id is taken from the checked subscription plan input
				const checkedPlan = this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked')
				if (checkedPlan) {
					const planVariantId = checkedPlan.dataset.variantId
					const resolvedPlanId = checkedPlan.value.toString().split('_')[0]
					if (resolvedPlanId) {
						formData.set('selling_plan', resolvedPlanId)
						if (this.elements.hiddenSellingPlan) this.elements.hiddenSellingPlan.value = resolvedPlanId
					}
					if (planVariantId) {
						formData.set('id', planVariantId)
						if (this.elements.hiddenVariantId) this.elements.hiddenVariantId.value = planVariantId
					}
				}
			} else {
				// One-time purchase: explicitly remove any selling plan
				formData.delete('selling_plan')
				if (this.elements.hiddenSellingPlan) this.elements.hiddenSellingPlan.value = ''

				// Ensure variant ID is set for one-time purchases
				if (this.currentSelections.variant?.id) {
					formData.set('id', this.currentSelections.variant.id)
					if (this.elements.hiddenVariantId) this.elements.hiddenVariantId.value = this.currentSelections.variant.id
				}
			}

			// Submit to Shopify cart
			fetch('/cart/add.js', {
				method: 'POST',
				body: formData
			})
			.then(response => {
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`)
				}
				return response.json()
			})
			.then(data => {
				
				// Trigger cart update events
				this.triggerCartUpdateEvents(data)
				
				// Update cart count if available
				this.updateCartCount()

				
				// Redirect to cart page
				const currentUrl = new URL(window.location.href);
				if (currentUrl.searchParams.get('view_cart') === null) {
					currentUrl.searchParams.append('view_cart', true);
				}
				// window.location.href = currentUrl.toString();
			})
			.catch(error => {
				console.error("❌ Error adding to cart:", error)
				// Don't show error popup since we're redirecting to cart
				// Just log the error for debugging
			})
			.finally(() => {
				// Restore button state
				addToCartButton.disabled = false
				addToCartButton.style.opacity = "1"
				buttonText.innerHTML = "ADD TO CART"
			})

		} catch (error) {
			console.error("❌ Unexpected error in handleAddToCart:", error)
			this.showAddToCartError("An unexpected error occurred")
		}
	}

	triggerCartUpdateEvents(cartData) {
		// Trigger custom event for cart update
		const cartUpdateEvent = new CustomEvent('cart:updated', {
			detail: { cart: cartData },
			bubbles: true
		})
		document.dispatchEvent(cartUpdateEvent)

		// Trigger Shopify's cart update event if available
		if (window.Shopify && window.Shopify.onCartUpdate) {
			window.Shopify.onCartUpdate(cartData)
		}
	}

	showAddToCartSuccess() {
		// Create success notification
		const notification = document.createElement('div')
		notification.className = 'add-to-cart-success'
		notification.innerHTML = `
			<div class="success-message">
				<svg class="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<path d="M20 6L9 17l-5-5"/>
				</svg>
				<span>Added to cart successfully!</span>
			</div>
		`
		
		// Style the notification
		notification.style.cssText = `
			position: fixed;
			top: 20px;
			right: 20px;
			background: #4CAF50;
			color: white;
			padding: 16px 20px;
			border-radius: 8px;
			box-shadow: 0 4px 12px rgba(0,0,0,0.15);
			z-index: 1000;
			transform: translateX(100%);
			transition: transform 0.3s ease;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		`
		
		// Add to page
		document.body.appendChild(notification)
		
		// Animate in
		setTimeout(() => {
			notification.style.transform = 'translateX(0)'
		}, 100)
		
		// Remove after 3 seconds
		setTimeout(() => {
			notification.style.transform = 'translateX(100%)'
			setTimeout(() => {
				if (notification.parentNode) {
					notification.parentNode.removeChild(notification)
				}
			}, 300)
		}, 3000)
	}

	showAddToCartError(errorMessage) {
		// Create error notification
		const notification = document.createElement('div')
		notification.className = 'add-to-cart-error'
		notification.innerHTML = `
			<div class="error-message">
				<svg class="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<path d="M18 6L6 18M6 6l12 12"/>
				</svg>
				<span>Error: ${errorMessage}</span>
			</div>
		`
		
		// Style the notification
		notification.style.cssText = `
			position: fixed;
			top: 20px;
			right: 20px;
			background: #f44336;
			color: white;
			padding: 16px 20px;
			border-radius: 8px;
			box-shadow: 0 4px 12px rgba(0,0,0,0.15);
			z-index: 1000;
			transform: translateX(100%);
			transition: transform 0.3s ease;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		`
		
		// Add to page
		document.body.appendChild(notification)
		
		// Animate in
		setTimeout(() => {
			notification.style.transform = 'translateX(0)'
		}, 100)
		
		// Remove after 5 seconds
		setTimeout(() => {
			notification.style.transform = 'translateX(100%)'
			setTimeout(() => {
				if (notification.parentNode) {
					notification.parentNode.removeChild(notification)
				}
			}, 300)
		}, 5000)
	}

	updateCartCount() {
		// Update cart count in header if available
		const cartCountElements = document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-count]')
		cartCountElements.forEach(element => {
			// This would typically fetch the current cart count from Shopify
			// For now, we'll just trigger a refresh event
			const refreshEvent = new CustomEvent('cart:refresh', { bubbles: true })
			element.dispatchEvent(refreshEvent)
		})
	}

	updateVariantForCurrentSelections() {
		
		this.updatePeachDescriptor()
	}

	isSupplyHidden(flavor, monthSupply) {
		if (typeof window.cnGetHiddenSupply === 'function') {
			return window.cnGetHiddenSupply(flavor) === monthSupply
		}
		return false
	}

	getSupplyFallbackOrder(currentMonthSupply) {
		// Get all unique supplies sorted by month count ascending
		const allSupplies = [...new Set(this.productData.variants.map(v => v.option2))]
			.sort((a, b) => parseInt(a) - parseInt(b))

		const currentIndex = allSupplies.indexOf(currentMonthSupply)
		if (currentIndex === -1) return allSupplies

		// Same first, then less (descending from current), then more (ascending from current)
		const less = allSupplies.slice(0, currentIndex).reverse()
		const more = allSupplies.slice(currentIndex + 1)
		return [currentMonthSupply, ...less, ...more]
	}

	// Helper method to find available variants for a specific flavor
	findAvailableVariant(flavor, addon, monthSupply, purchaseType) {
		if (purchaseType === "subscribe") {
			return this.productData.variants.find(
				(v) => 
					v.option1 === flavor && 
					v.option3 === addon && 
					v.option2 === monthSupply && 
					v.available
			)
		} else {
			return this.productData.variants.find(
				(v) => 
					v.option1 === flavor && 
					v.option3 === addon && 
					v.option2 === monthSupply && 
					v.available
			)
		}
	}

	// Helper method to get all available flavors for current selections
	getAvailableFlavors(addon, monthSupply, purchaseType) {
		const availableFlavors = new Set()


		this.productData.variants.forEach(variant => {
			if (variant.available &&
				variant.option3 === addon &&
				variant.option2 === monthSupply) {
				availableFlavors.add(variant.option1)
			}
		})

		const result = Array.from(availableFlavors)
		return result
	}

	// Helper method to find the first available subscription plan option
	findFirstAvailablePlanOption() {

		const planInputs = Array.from(this.elements.planInputs || [])
		const currentFlavor = this.currentSelections.flavor
		const currentAddon = this.currentSelections.addon

		// First, try to find a plan that matches current flavor and addon
		let availablePlan = planInputs.find(radio => {
			const planOption = radio.closest('.plan-option')
			if (planOption?.classList.contains('hidden') || radio.disabled) {
				return false
			}

			const planVariantId = radio.dataset.variantId
			const planVariant = this.productData.variants.find(v => v.id?.toString() === planVariantId?.toString())

			return planVariant &&
				   planVariant.available &&
				   planVariant.option1 === currentFlavor &&
				   planVariant.option3 === currentAddon
		})

		// If no matching plan found, get the first visible and available plan
		if (!availablePlan) {
			availablePlan = planInputs.find(radio => {
				const planOption = radio.closest('.plan-option')
				return !planOption?.classList.contains('hidden') && !radio.disabled
			})
		}

		return availablePlan
	}

	// Helper method to find the first available variant option
	findFirstAvailableVariantOption() {

		const variantInputs = Array.from(this.elements.variantInputs || [])
		const currentFlavor = this.currentSelections.flavor
		const currentAddon = this.currentSelections.addon

		// First, try to find a variant that matches current flavor and addon
		let availableVariant = variantInputs.find(radio => {
			const variantOption = radio.closest('.variant-option')
			if (variantOption?.classList.contains('hidden') || radio.disabled) {
				return false
			}

			const variantId = radio.value
			const variant = this.productData.variants.find(v => v.id?.toString() === variantId?.toString())

			return variant &&
				   variant.available &&
				   variant.option1 === currentFlavor &&
				   variant.option3 === currentAddon
		})

		// If no matching variant found, get the first visible and available variant
		if (!availableVariant) {
			availableVariant = variantInputs.find(radio => {
				const variantOption = radio.closest('.variant-option')
				return !variantOption?.classList.contains('hidden') && !radio.disabled
			})
		}

		return availableVariant
	}

	updateFlavorVisualSelection(flavor) {
		this.form.querySelectorAll(".flavor-option").forEach((option) => {
			option.classList.remove("selected")
		})

		const selectedOption = this.form.querySelector(`.flavor-option[data-flavor="${flavor}"]`)
		if (selectedOption) {
			selectedOption.classList.add("selected")
		}
	}

	updatePurchaseType(purchaseType) {
		
		if (!this.currentSelections) {
			return
		}

		// Preserve current selections
		const preservedSelections = {
			flavor: this.currentSelections.flavor,
			addon: this.currentSelections.addon,
			monthSupply: this.currentSelections.monthSupply
		}


		this.currentSelections.purchaseType = purchaseType


		if (this.elements?.purchaseTypeTabs) {
			this.elements.purchaseTypeTabs.forEach((tab) => {
				tab.classList.remove("active")
			})
		}

		const activeTab = this.form?.querySelector(`[data-purchase-type="${purchaseType}"]`)
		if (activeTab) {
			activeTab.classList.add("active")
		}

		// Update protection sections visibility
		const onetimeProtection = this.form?.querySelector('.out_of_stock_protection.onetime-views')
		const subscribeProtection = this.form?.querySelector('.protection-section.subscribe-views2')
		const outOfStockProtectionLine = this.form?.querySelector('.out_of_stock_protection_line')
		const outOfStockProtectionText = this.form?.querySelector('[data-out-of-stock-protection-text]')
		const inStockProtectionText = this.form?.querySelector('[data-in-stock-protection-text]')

		if (onetimeProtection) {
			if (purchaseType === 'onetime') {
				onetimeProtection.classList.remove('hidden')
			} else {
				onetimeProtection.classList.add('hidden')
			}
		}

		if (subscribeProtection) {
			if (purchaseType === 'subscribe') {
				subscribeProtection.classList.remove('hidden')
			} else {
				subscribeProtection.classList.add('hidden')
			}
		}

		// Handle out_of_stock_protection_line visibility based on purchase type
		if (outOfStockProtectionLine) {
			if (purchaseType === 'onetime') {
				outOfStockProtectionText.classList.remove('hidden')
				inStockProtectionText.classList.add('hidden')
			} else {
				outOfStockProtectionText.classList.add('hidden')
				inStockProtectionText.classList.remove('hidden')
			}
		}


		// This will automatically adjust flavor if needed
		this.updateVariantForCurrentSelections()
		this.updateDisplay()
		this.updateVariantVisualSelection()
		this.updatePricing()
		this.updateFormFields()
		this.syncOptionsCorrector()
	}

	updateSubscriptionPlan(planId) {
		const normalizedId = planId?.toString().split("_")[0]
		const plan = this.productData.subscriptionPlans.find((p) => p.id?.toString() === normalizedId)
		if (plan) {
			this.currentSelections.subscriptionPlan = plan
		}
	}

	updateVariant(variantId) {
		const variant = this.productData.variants.find((v) => v.id.toString() === variantId.toString())

		if (variant) {
			this.currentSelections.variant = variant
			// Update month supply from variant
			if (variant.option2) {
				this.currentSelections.monthSupply = variant.option2
			}
		}

		// Update peach descriptor visibility
		this.updatePeachDescriptor()
	}

	deselectOtherVariantOptions(selectedVariantId) {
		// Find the selected variant to get its flavor and addon
		const selectedVariant = this.productData.variants.find((v) => v.id.toString() === selectedVariantId.toString())
		if (!selectedVariant) return

		const selectedFlavor = selectedVariant.option1
		const selectedAddon = selectedVariant.option3

		// Deselect all other variant options with the same flavor and addon
		this.form.querySelectorAll('.variant-option').forEach((option) => {
			const optionFlavor = option.dataset.variantFlavor
			const optionAddon = option.dataset.variantAddon
			const optionVariantId = option.dataset.variantId

			// If this option has the same flavor and addon but different variant ID, deselect it
			if (optionFlavor === selectedFlavor && 
				optionAddon === selectedAddon && 
				optionVariantId !== selectedVariantId) {
				
				// Uncheck the radio button
				const radio = option.querySelector('input[type="radio"]')
				if (radio) {
					radio.checked = false
					radio.removeAttribute('checked')
				}
				
				// Remove selected class
				option.classList.remove('selected')
			}
		})
	}

	deselectOtherPlanOptions(selectedPlanId) {
		// Deselect all other plan options with different plan IDs
		this.form.querySelectorAll('.plan-option').forEach((option) => {
			const optionPlanId = option.dataset.planId
			if (optionPlanId && optionPlanId !== selectedPlanId) {
				// Uncheck the radio button
				const radio = option.querySelector('input[type="radio"]')
				if (radio) {
					radio.checked = false
					radio.removeAttribute('checked')
				}
				// Remove selected class
				option.classList.remove('selected')
			}
		})
	}

	// Helper method to get the selected option identifier (month supply)
	getSelectedOptionIdentifier() {
		let selectedOptionIdentifier = null

		if (this.currentSelections.purchaseType === "subscribe") {
			const currentlyCheckedOption = this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked')
			if (currentlyCheckedOption) {
				// For subscriptions, use the option2 attribute (month supply) as identifier
				selectedOptionIdentifier = currentlyCheckedOption.getAttribute('option2')
			}
		} else {
			const currentlyCheckedOption = this.form.querySelector('.onetime-options input[name="variant_id"]:checked')
			if (currentlyCheckedOption) {
				// For one-time purchases, use the month supply from the closest variant option
				const variantOption = currentlyCheckedOption.closest('.variant-option')
				selectedOptionIdentifier = currentlyCheckedOption.getAttribute('option2')
			}
		}

		return selectedOptionIdentifier
	}

	// Helper method to set up pending selection restore
	setPendingSelectionRestore() {

		const selectedOptionIdentifier = this.getSelectedOptionIdentifier()
			this.pendingSelectionRestore = {
				selectedOptionIdentifier: this.currentSelections.monthSupply,
				preservedSelections: {
					flavor: this.currentSelections.flavor,
					addon: this.currentSelections.addon,
					monthSupply: this.currentSelections.monthSupply,
					purchaseType: this.currentSelections.purchaseType,
					variant: this.currentSelections.variant,
					subscriptionPlan: this.currentSelections.subscriptionPlan
				}
			}
	}

	updateAddon(addonValue) {
		// Preserve current selections including the currently selected option
		const preservedSelections = {
			flavor: this.currentSelections.flavor,
			monthSupply: this.currentSelections.monthSupply,
			purchaseType: this.currentSelections.purchaseType,
			variant: this.currentSelections.variant,
			subscriptionPlan: this.currentSelections.subscriptionPlan
		}


		// Set up pending selection restore BEFORE clearing selections
		this.setPendingSelectionRestore()

		this.currentSelections.addon = addonValue

		if (this.elements.addonSelect) {
			this.elements.addonSelect.value = addonValue
		}

		// Clear variant and subscription plan selections when changing addon
		this.currentSelections.variant = null
		this.currentSelections.subscriptionPlan = null

		// Deselect all variant and plan options
		this.form.querySelectorAll('.variant-option, .plan-option').forEach((option) => {
			const radio = option.querySelector('input[type="radio"]')
			if (radio) {
				radio.checked = false
				radio.removeAttribute('checked')
			}
			option.classList.remove('selected')
		})

		// Restore preserved selections
		this.currentSelections.flavor = preservedSelections?.flavor
		this.currentSelections.monthSupply = preservedSelections?.monthSupply
		this.currentSelections.purchaseType = preservedSelections?.purchaseType


		// Clear subscription plan if it's no longer valid for this addon
		if (
			this.currentSelections.purchaseType === "subscribe" &&
			this.currentSelections.subscriptionPlan
		) {
			const planStillValid = this.productData.variants.some(
				(v) =>
					v.option1 === this.currentSelections.flavor &&
					v.option3 === addonValue &&
					v.selling_plan_allocations?.some(
						(spa) =>
							spa.selling_plan_id.toString() ===
							this.currentSelections.subscriptionPlan.id.toString()
					) &&
					v.available
			)

			if (!planStillValid) {
				this.currentSelections.subscriptionPlan = null
			}
		}

		this.updateVariantForCurrentSelections()
		this.updateDisplay()

		// Universal restore will happen automatically in updateDisplay()

		this.updatePricing()
		this.updateFormFields()

		// Ensure the selected class is properly applied
		this.ensureSingleSelection()
		this.syncOptionsCorrector()
		// Update peach descriptor visibility

	}

	// Universal method to restore the previously selected option - called LAST after all display updates
	restorePreviousSelectionUniversal() {
		// Only restore if we have a pending selection to restore
		if (!this.pendingSelectionRestore) {
			return false
		}

		// Safe destructuring with fallback
		const {
			selectedOptionIdentifier,
			preservedSelections
		} = this.pendingSelectionRestore || {}

		if (!selectedOptionIdentifier) {
			this.pendingSelectionRestore = null
			return false
		}


		let matchingOption = null

		if (preservedSelections?.purchaseType === "subscribe") {
			// Look for subscription plan with matching month supply (skip hidden supplies)
			matchingOption = Array.from(this.form.querySelectorAll('.subscription-plans input[name="selling_plan_ui"]'))
				.find(radio => {
					const planOption = radio.closest('.plan-option')
					const radioMonthSupply = radio.getAttribute('option2')
					return radioMonthSupply === selectedOptionIdentifier &&
						   !this.isSupplyHidden(this.currentSelections.flavor, radioMonthSupply) &&
						   planOption &&
						   !planOption.classList.contains('hidden') &&
						   !radio.disabled
				})
		} else {
			// Look for one-time variant with matching month supply (skip hidden supplies)
			matchingOption = Array.from(this.form.querySelectorAll('.onetime-options input[name="variant_id"]'))
				.find(radio => {
					const variantOption = radio.closest('.variant-option')
					const radioMonthSupply = variantOption?.dataset?.monthSupply || radio.getAttribute('option2')
					return radioMonthSupply === selectedOptionIdentifier &&
						   !this.isSupplyHidden(this.currentSelections.flavor, radioMonthSupply) &&
						   variantOption &&
						   !variantOption.classList.contains('hidden') &&
						   !radio.disabled
				})
		}

		if (matchingOption) {
			// Found the exact same option, select it
			matchingOption.checked = true
			matchingOption.setAttribute('checked', 'checked')

			if (preservedSelections?.purchaseType === "subscribe") {
				const resolvedPlanId = matchingOption.value.toString().split('_')[0]
				this.updateSubscriptionPlan(resolvedPlanId)
				const planVariantId = matchingOption.dataset.variantId
				if (planVariantId) {
					this.updateVariant(planVariantId)
				}
			} else {
				this.updateVariant(matchingOption.value)
			}

			this.pendingSelectionRestore = null // Clear the pending restore
			return true // Selection was restored
		} else {
			this.pendingSelectionRestore = null // Clear the pending restore
			return false // No selection was restored
		}
	}

	updateDisplay() {
		if (!this.currentSelections || !this.elements) {
			return
		}

		const { flavor, purchaseType, addon, monthSupply } = this.currentSelections

		if (this.elements.subscriptionPlans && this.elements.onetimeOptions) {
			if (purchaseType === "subscribe") {
				this.elements.subscriptionPlans.classList.remove("hidden")
				this.elements.onetimeOptions.classList.add("hidden")

				// JS-only filter: show only plan options whose underlying variant has the currently selected option3 (addon)
				this.elements.subscriptionPlans.querySelectorAll(".plan-option").forEach((planOption) => {
					const radio = planOption.querySelector('input[type="radio"][data-variant-id]')
					let matchesOption3 = false
					if (radio) {
						const planVariantId = radio.dataset.variantId
						const planVariant = this.productData.variants.find(
							(v) => v.id?.toString() === planVariantId?.toString()
						)
						matchesOption3 =
							!!planVariant && planVariant.option3 === addon && planVariant.option1 === flavor
					}
					const planMonthSupply = radio ? this.productData.variants.find(v => v.id?.toString() === radio.dataset.variantId?.toString())?.option2 : null
					if (matchesOption3 && !this.isSupplyHidden(flavor, planMonthSupply)) {
						planOption.classList.remove("hidden")
						if (radio) radio.disabled = planOption.dataset.available === 'false'
					} else {
						planOption.classList.add("hidden")
						if (radio) radio.disabled = true
					}
				})
				// Auto-select plan: prioritize matching monthSupply, then first available
				const allPlanRadios = Array.from(
					this.elements.subscriptionPlans.querySelectorAll(
						'input[type="radio"][name="selling_plan_ui"]'
					)
				)
				let checked = allPlanRadios.find(
					(r) => r.checked && !r.closest(".plan-option")?.classList.contains("hidden") && !r.disabled
				)
				
				// If a plan is already checked, verify it matches the preserved monthSupply
				if (checked) {
					const checkedMonthSupply = checked.getAttribute('option2')
					if (monthSupply && checkedMonthSupply !== monthSupply) {
						checked = null // Clear it so we can find the correct one
					}
				}
				
				if (!checked) {
					// First try to find a plan that matches the preserved monthSupply (skip hidden supplies)
					if (monthSupply) {
						checked = allPlanRadios.find(
							(r) => !r.closest(".plan-option")?.classList.contains("hidden") &&
							!r.disabled &&
							r.getAttribute('option2') === monthSupply &&
							!this.isSupplyHidden(flavor, r.getAttribute('option2'))
						)
						if (checked) {
						}
					}

					// If no match found, select first available (skip hidden supplies)
					if (!checked) {
						checked = allPlanRadios.find(
							(r) => !r.closest(".plan-option")?.classList.contains("hidden") &&
							!r.disabled &&
							!this.isSupplyHidden(flavor, r.getAttribute('option2'))
						)
						if (checked) {
						}
					}
					
					if (checked) {
						const planOption = checked.closest('.plan-option')
						if (planOption) {
							planOption.classList.add("selected")
						}
						checked.checked = true
						checked.setAttribute("checked", "checked")
					}
				}
				if (checked) {
					const foundMonthSupply = checked.getAttribute('option2')
					
					// If the found plan doesn't match preserved monthSupply, check if preserved is available
					if (this.currentSelections.monthSupply && this.currentSelections.monthSupply !== foundMonthSupply) {
						const preservedPlan = allPlanRadios.find(
							(r) => !r.closest(".plan-option")?.classList.contains("hidden") &&
							!r.disabled &&
							r.getAttribute('option2') === this.currentSelections.monthSupply &&
							!this.isSupplyHidden(flavor, r.getAttribute('option2'))
						)
						if (preservedPlan) {
							// Preserved monthSupply is available, select that plan instead
							checked.checked = false
							checked.removeAttribute('checked')
							checked.closest('.plan-option')?.classList.remove('selected')
							
							checked = preservedPlan
							const planOption = checked.closest('.plan-option')
							if (planOption) {
								planOption.classList.add("selected")
							}
							checked.checked = true
							checked.setAttribute("checked", "checked")
						} else {
							// Preserved monthSupply is not available, use the found one
							this.currentSelections.monthSupply = foundMonthSupply
						}
					} else {
						// They match or no preserved monthSupply, keep current
						if (!this.currentSelections.monthSupply) {
							this.currentSelections.monthSupply = foundMonthSupply
						}
					}
					
					// Sync hidden selling_plan and variant/currentSelections
					const resolvedPlanId = checked.value.toString().split("_")[0]
					if (this.elements.globalSellingPlanSelect) {
						this.elements.globalSellingPlanSelect.value = resolvedPlanId
					}
					const planVariantId = checked.dataset.variantId
					if (planVariantId) {
						this.updateVariant(planVariantId)
					}
					this.updateSubscriptionPlan(resolvedPlanId)
					
					if (this.lastMonthSupplyByType) {
						this.lastMonthSupplyByType.subscribe = this.currentSelections.monthSupply
					}
				}
			} else {
				this.elements.subscriptionPlans.classList.add("hidden")
				this.elements.onetimeOptions.classList.remove("hidden")
			}
		}

		if (this.elements.onetimeOptions) {
			this.elements.onetimeOptions.querySelectorAll(".variant-option").forEach((option) => {
				const variantFlavor = option.dataset.variantFlavor
				const variantAddon = option.dataset.variantAddon
				const flavorMatch = variantFlavor === flavor
				const addonMatch = variantAddon === addon

				// Show all variants for the selected flavor (skip supply-hidden tiers)
				if (flavorMatch && addonMatch && !this.isSupplyHidden(flavor, option.dataset.monthSupply)) {
					option.classList.remove("hidden")
					// Also enable the radio button
					const radio = option.querySelector('input[type="radio"]')
					if (radio) {
						radio.disabled = option.dataset.available === 'false'
					}
				} else {
					option.classList.add("hidden")
					// Disable the radio button
					const radio = option.querySelector('input[type="radio"]')
					if (radio) {
						radio.disabled = true
					}
				}
			})

			// Auto-select variant: prioritize matching monthSupply, then first available
			const allVariantRadios = Array.from(
				this.elements.onetimeOptions.querySelectorAll(
					'input[type="radio"][name="variant_id"]'
				)
			)
			let checkedVariant = allVariantRadios.find(
				(r) => r.checked && !r.closest(".variant-option")?.classList.contains("hidden") && !r.disabled
			)
			
			// If a variant is already checked, verify it matches the preserved monthSupply
			if (checkedVariant) {
				const checkedMonthSupply = checkedVariant.closest('.variant-option')?.dataset?.monthSupply
				if (monthSupply && checkedMonthSupply !== monthSupply) {
					checkedVariant = null // Clear it so we can find the correct one
				}
			}
			
			if (!checkedVariant) {
				// First try to find a variant that matches the preserved monthSupply (skip hidden supplies)
				if (monthSupply) {
					checkedVariant = allVariantRadios.find(
						(r) => !r.closest(".variant-option")?.classList.contains("hidden") &&
						!r.disabled &&
						r.closest('.variant-option')?.dataset?.monthSupply === monthSupply &&
						!this.isSupplyHidden(flavor, r.closest('.variant-option')?.dataset?.monthSupply)
					)
					if (checkedVariant) {
					}
				}

				// If no match found, select first available (skip hidden supplies)
				if (!checkedVariant) {
					checkedVariant = allVariantRadios.find(
						(r) => !r.closest(".variant-option")?.classList.contains("hidden") &&
						!r.disabled &&
						!this.isSupplyHidden(flavor, r.closest('.variant-option')?.dataset?.monthSupply)
					)
					if (checkedVariant) {
					}
				}
				
				if (checkedVariant) {
					const variantOption = checkedVariant.closest('.variant-option')
					if (variantOption) {
						variantOption.classList.add("selected")
					}
					checkedVariant.checked = true
					checkedVariant.setAttribute("checked", "checked")
				}
			}
		if (checkedVariant) {
			const foundMonthSupply = checkedVariant.closest('.variant-option')?.dataset?.monthSupply || null
			
			// If the found variant doesn't match preserved monthSupply, check if preserved is available
			if (this.currentSelections.monthSupply && this.currentSelections.monthSupply !== foundMonthSupply) {
				const preservedVariantRadio = allVariantRadios.find(
					(r) => !r.closest(".variant-option")?.classList.contains("hidden") &&
					!r.disabled &&
					r.closest('.variant-option')?.dataset?.monthSupply === this.currentSelections.monthSupply &&
					!this.isSupplyHidden(flavor, r.closest('.variant-option')?.dataset?.monthSupply)
				)
				if (preservedVariantRadio) {
					// Preserved monthSupply is available, select that variant instead
					checkedVariant.checked = false
					checkedVariant.removeAttribute('checked')
					checkedVariant.closest('.variant-option')?.classList.remove('selected')
					
					checkedVariant = preservedVariantRadio
					const variantOption = checkedVariant.closest('.variant-option')
					if (variantOption) {
						variantOption.classList.add("selected")
					}
					checkedVariant.checked = true
					checkedVariant.setAttribute("checked", "checked")
				} else {
					// Preserved monthSupply is not available, use the found one
					this.currentSelections.monthSupply = foundMonthSupply
				}
			} else {
				// They match or no preserved monthSupply, keep current
				if (!this.currentSelections.monthSupply) {
					this.currentSelections.monthSupply = foundMonthSupply
				}
			}
			
			// Update variant
			this.updateVariant(checkedVariant.value)
			
			if (this.lastMonthSupplyByType) {
				this.lastMonthSupplyByType.onetime = this.currentSelections.monthSupply
			}
		}
		}

		this.updateVariantVisualSelection()
		
		// Sync dropdown2 with current addon selection
		if (this.currentSelections.addon) {
			this.syncDropdown2WithAddonSelect(this.currentSelections.addon)
		}

		// Update peach descriptor visibility
		this.updatePeachDescriptor()

		// 🔥 UNIVERSAL SELECTION RESTORE - FIRES LAST AFTER ALL DISPLAY UPDATES
		this.restorePreviousSelectionUniversal()
		
		// Check if selected variant is disabled and auto-select next available
		this.checkAndAutoSelectAvailableVariant()
	}

	// Update peach descriptor visibility based on flavor and month supply
	updatePeachDescriptor() {
		
		// Find all peach descriptors
		const peachDescriptors = this.form.querySelectorAll('.peach-descriptor')
		if (peachDescriptors.length === 0) {
			return
		}

		// Check if Variety flavor is selected
		const varietyRadio = this.form.querySelector('.flavor-option[data-flavor="Variety"] input[type="radio"]:checked')
		const isVarietySelected = varietyRadio !== null

		// Check what month supply is currently selected
		// First determine purchase type by checking which section is visible
		const subscriptionPlansSection = this.form.querySelector('.subscription-plans')
		const onetimeOptionsSection = this.form.querySelector('.onetime-options')
		const isSubscribeMode = subscriptionPlansSection && !subscriptionPlansSection.classList.contains('hidden')
		const isOnetimeMode = onetimeOptionsSection && !onetimeOptionsSection.classList.contains('hidden')
		
		
		let selectedMonthSupply = null
		
		// Check based on which mode is active
		if (isSubscribeMode) {
			// Check subscription plans
			const checkedPlan = this.form.querySelector('.subscription-plans input[type="radio"][name="selling_plan_ui"]:checked')
			if (checkedPlan) {
				selectedMonthSupply = checkedPlan.getAttribute('option2')
			} else {
			}
		} else if (isOnetimeMode) {
			// Check one-time variants
			const checkedVariant = this.form.querySelector('.onetime-options input[type="radio"][name="variant_id"]:checked')
			if (checkedVariant) {
				const variantOption = checkedVariant.closest('.variant-option')
				selectedMonthSupply = variantOption?.dataset?.monthSupply || null
			} else {
			}
		} else {
			// Fallback: try both if we can't determine
			const checkedPlan = this.form.querySelector('.subscription-plans input[type="radio"][name="selling_plan_ui"]:checked')
			if (checkedPlan) {
				selectedMonthSupply = checkedPlan.getAttribute('option2')
			} else {
				const checkedVariant = this.form.querySelector('.onetime-options input[type="radio"][name="variant_id"]:checked')
				if (checkedVariant) {
					const variantOption = checkedVariant.closest('.variant-option')
					selectedMonthSupply = variantOption?.dataset?.monthSupply || null
				}
			}
		}


		// Show descriptor ONLY when Variety is selected AND month supply is exactly "3 Month Supply"
		const shouldShow = isVarietySelected && selectedMonthSupply === '3 Month Supply'

		// Update all peach descriptors
		peachDescriptors.forEach((descriptor, index) => {
			if (shouldShow) {
				descriptor.style.display = ''
				descriptor.classList.remove('hidden')
			} else {
				descriptor.style.display = 'none'
				descriptor.classList.add('hidden')
			}
		})
		
	}

	updateVariantVisualSelection() {
		// Use the checked radio as ground truth — avoids remove-all-then-re-add repaint flash
		let correctOption = null
		if (this.currentSelections.purchaseType === "subscribe") {
			const checkedPlan = this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked')
			if (checkedPlan) correctOption = checkedPlan.closest('.plan-option')
		} else {
			const checkedVariant = this.form.querySelector('.onetime-options input[name="variant_id"]:checked')
			if (checkedVariant) correctOption = checkedVariant.closest('.variant-option')
		}

		this.form.querySelectorAll(".variant-option, .plan-option").forEach((option) => {
			if (option === correctOption) {
				option.classList.add("selected")
			} else {
				option.classList.remove("selected")
			}
		})

		this.ensureSingleSelection()
	}

	ensureSingleSelection() {
		// This method ensures that only one option (variant or plan) is visually selected
		// based on the radio button state. It's particularly useful for subscriptions
		// where multiple plan options might be visible, but only one should be selected.
		if (this.currentSelections.purchaseType === "subscribe") {
			// For subscriptions, ensure only one plan option is selected
			const checkedPlanRadio = document.querySelector(
				'.subscription-plans input[name="selling_plan_ui"]:checked'
			)
			if (checkedPlanRadio) {
				const checkedPlanOption = checkedPlanRadio.closest('.plan-option')
				if (checkedPlanOption) {
					// Check if the selected plan is available
					const isAvailable = checkedPlanOption.dataset.available !== 'false'

					// First remove selected class from all plan options
					this.form.querySelectorAll('.plan-option').forEach((option) => {
						option.classList.remove('selected')
					})

					// Only add selected class if the plan is available
					if (isAvailable) {
						checkedPlanOption.classList.add('selected')
					} else {
						// Uncheck the unavailable radio button
						checkedPlanRadio.checked = false
						checkedPlanRadio.removeAttribute('checked')

						// Find the next visible and available plan option
						const allPlanRadios = Array.from(
							this.form.querySelectorAll('.subscription-plans input[name="selling_plan_ui"]:not([disabled])')
						).filter(radio => {
							const planOption = radio.closest('.plan-option')
							return planOption && planOption.dataset.available !== 'false' && !planOption.classList.contains('hidden')
						})

						if (allPlanRadios.length > 0) {
							const nextAvailableRadio = allPlanRadios[0]
							nextAvailableRadio.checked = true
							nextAvailableRadio.setAttribute('checked', 'checked')

							const nextPlanOption = nextAvailableRadio.closest('.plan-option')
							if (nextPlanOption) {
								nextPlanOption.classList.add('selected')
							}
						} else {
						}
					}
				}
			}
		} else {
			// For one-time purchases, ensure only one variant option is selected
			const checkedVariantRadio = document.querySelector(
				'.onetime-options input[name="variant_id"]:checked'
			)
			if (checkedVariantRadio) {
				const checkedVariantOption = checkedVariantRadio.closest('.variant-option')
				if (checkedVariantOption) {
					// Check if the selected variant is available
					const isAvailable = checkedVariantOption.dataset.available !== 'false'

					this.form.querySelectorAll('.variant-option').forEach((option) => {
						if (option !== checkedVariantOption) {
							option.classList.remove('selected')
						}
					})

					// Only add selected class if the variant is available
					if (isAvailable) {
						checkedVariantOption.classList.add('selected')
					} else {
						// Uncheck the unavailable radio button
						checkedVariantRadio.checked = false
						checkedVariantRadio.removeAttribute('checked')
						// Remove selected class from the unavailable option
						checkedVariantOption.classList.remove('selected')

						// Find the next visible and available variant option
						const allVariantRadios = Array.from(
							this.form.querySelectorAll('.onetime-options input[name="variant_id"]:not([disabled])')
						).filter(radio => {
							const variantOption = radio.closest('.variant-option')
							return variantOption && variantOption.dataset.available !== 'false' && !variantOption.classList.contains('hidden')
						})

						if (allVariantRadios.length > 0) {
							const nextAvailableRadio = allVariantRadios[0]
							nextAvailableRadio.checked = true
							nextAvailableRadio.setAttribute('checked', 'checked')

							const nextVariantOption = nextAvailableRadio.closest('.variant-option')
							if (nextVariantOption) {
								nextVariantOption.classList.add('selected')
							}
						} else {
						}
					}
				}
			}
		}
	}

	updatePricing() {
		try {
			if (!this.currentSelections) {
				return
			}

			// Find the exact variant that matches current selections
			const targetVariant = this.findExactVariant()

			if (!targetVariant) {
				console.error("No variant found for current selections:", this.currentSelections)
				return
			}

			// SIMPLE PRICING LOGIC
			let finalPrice = targetVariant.price / 100 // Convert from cents
			let originalPrice = targetVariant.compare_at_price / 100 // Convert from cents

			// For subscriptions, use selling plan pricing if available
			if (
				this.currentSelections.purchaseType === "subscribe" &&
				targetVariant.selling_plan_allocations?.[0]
			) {
				const sellingPlan = targetVariant.selling_plan_allocations[0]
				finalPrice = sellingPlan.price / 100
			}
			// Update display
			if (this.elements?.finalTotal) {
				this.elements.finalTotal.textContent = this.formatMoney(finalPrice)
			}

			if (this.elements?.originalTotal) {
				// Hide original total if same as final
				if (Math.round(originalPrice * 100) === Math.round(finalPrice * 100) || !originalPrice) {
					this.elements.originalTotal.textContent = ''
					this.elements.originalTotal.style.display = 'none'
				} else {
					this.elements.originalTotal.textContent = this.formatMoney(originalPrice)
					this.elements.originalTotal.style.display = ''
				}
			}
		} catch (error) {
			console.error("Error updating pricing:", error)
		}
	}

	findExactVariant() {
		if (!this.productData?.variants) {
			return null
		}

		const { flavor, addon, purchaseType, subscriptionPlan, variant, monthSupply } = this.currentSelections

		// SIMPLE LOGIC: For one-time, use current variant. For subscription, find matching variant.
		if (purchaseType === "onetime" && variant) {
			return variant
		}

		if (purchaseType === "subscribe" && subscriptionPlan) {
			// Find variant with this flavor + addon that has the subscription plan
			const checkedRadio = document.querySelector(
				'.subscription-plans input[name="selling_plan_ui"]:checked'
			)
			const selectedOption2 = checkedRadio ? checkedRadio.getAttribute("option2") : null
			const vars = window.productFormV2.variants.filter(
				(v) =>
					v.option1 === flavor &&
					v.option3 === addon &&
					(selectedOption2 ? v.option2 === selectedOption2 : true) &&
					v.available
			)

			return vars[0]
		}

		return null
	}

	updateFormFields() {
		try {
			if (!this.elements || !this.currentSelections) {
				return
			}

			const { variant, flavor, subscriptionPlan, purchaseType, addon } = this.currentSelections

			if (this.elements.hiddenVariantId) {
				const variantId = variant?.id || ""
				this.elements.hiddenVariantId.value = variantId
			} else {
				console.warn("⚠️ Hidden variant ID field not found")
			}

			if (this.elements.hiddenSellingPlan) {
				let sellingPlanId = ""

				if (purchaseType === "subscribe" && subscriptionPlan?.id) {
					sellingPlanId = subscriptionPlan.id
				}
				this.elements.hiddenSellingPlan.value = sellingPlanId

			}
			if (this.elements.hiddenFlavor) {
				this.elements.hiddenFlavor.value = flavor || ""
			}

			if (this.elements.hiddenAddon) {
				this.elements.hiddenAddon.value = addon || ""
			}

			this.validateRequiredFields()
		} catch (error) {
			console.error("Error updating form fields:", error)
		}
	}

	validateRequiredFields() {
		const warnings = []

		if (!this.elements.hiddenVariantId?.value) {
			warnings.push("Missing variant ID - cart submission will fail")
		}

		if (
			this.currentSelections.purchaseType === "subscribe" &&
			!this.elements.hiddenSellingPlan?.value
		) {
			warnings.push("Missing selling plan for subscription")
		}

		if (warnings.length > 0) {
			console.warn("Form validation warnings:", warnings)
			return false
		}

		return true
	}

	formatMoney(amount) {
		// Use Shopify's built-in money formatting if available
		if (window.Shopify && window.Shopify.formatMoney) {
			return window.Shopify.formatMoney(amount)
		}
		// Fallback to standard formatting
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	setupDropdown2() {
		const wrapperDropdown = this.form.querySelector('.wrapper-dropdown')
		if (!wrapperDropdown) return

		const wrapperBlock = wrapperDropdown.querySelector('.wrapper-block')
		const dropdown2 = wrapperDropdown.querySelector('.dropdown2')
		const items = dropdown2?.querySelectorAll('.item')
		if (!items) return

		// Handle clicks on wrapper-block to toggle dropdown
		if (wrapperBlock) {
			wrapperBlock.addEventListener('click', (e) => {
				if (wrapperDropdown.classList.contains('active')) {
					this.closeDropdown2()
				} else {
					this.openDropdown2()
				}
			})
		}

		// Handle clicks on dropdown2 items
		items.forEach(item => {
			item.addEventListener('click', (e) => {
				const value = item.dataset.value
				if (value) {
					// Update addon-select value
					if (this.elements.addonSelect) {
						this.elements.addonSelect.value = value
					}

					// Update current selections
					this.updateAddon(value)

					// Update visual state
					this.updateDropdown2VisualState(value)

					// Close dropdown2
					this.closeDropdown2()
				}
			})
		})

		// Close dropdown2 when clicking outside
		document.addEventListener('click', (e) => {
			if (!wrapperDropdown.contains(e.target) && !this.elements.addonSelect.contains(e.target)) {
				this.closeDropdown2()
			}
		})
	}

	openDropdown2() {
		const wrapperDropdown = this.form.querySelector('.wrapper-dropdown')
		if (wrapperDropdown) {
			wrapperDropdown.classList.add('active')
			
			// Rotate arrow
			const arrow = wrapperDropdown.querySelector('.arrow')
			if (arrow) {
				arrow.classList.add('rotated')
			}
		}
	}

	closeDropdown2() {
		const wrapperDropdown = this.form.querySelector('.wrapper-dropdown')
		if (wrapperDropdown) {
			wrapperDropdown.classList.remove('active')
			
			// Reset arrow rotation
			const arrow = wrapperDropdown.querySelector('.arrow')
			if (arrow) {
				arrow.classList.remove('rotated')
			}
		}
	}

	syncDropdown2WithAddonSelect(addonValue) {
		const wrapperDropdown = this.form.querySelector('.wrapper-dropdown')
		if (!wrapperDropdown) return

		// Update the display element
		const displayElement = wrapperDropdown.querySelector('.selected-display')
		if (displayElement && addonValue) {
			displayElement.textContent = addonValue
			displayElement.dataset.value = addonValue
		}

		// Update the active state of dropdown items
		const items = wrapperDropdown.querySelectorAll('.dropdown2 .item')
		items.forEach(item => {
			item.classList.remove('active')
			if (item.dataset.value === addonValue) {
				item.classList.add('active')
			}
		})
	}

	updateDropdown2VisualState(selectedValue) {
		const wrapperDropdown = this.form.querySelector('.wrapper-dropdown')
		if (!wrapperDropdown) return

		const items = wrapperDropdown.querySelectorAll('.dropdown2 .item')
		items.forEach(item => {
			item.classList.remove('active')
			if (item.dataset.value === selectedValue) {
				item.classList.add('active')
			}
		})
	}

	// Function to parse flavor availability data
	parseFlavorAvailability(availabilityString) {
		if (!availabilityString) return {};

		const flavors = {};
		const pairs = availabilityString.split(',');

		pairs.forEach(pair => {
			const [flavor, status] = pair.trim().split(':');
			if (flavor && status) {
				flavors[flavor.trim()] = status.trim();
			}
		});

		return flavors;
	}

	// Function to update flavor availability display
	updateFlavorAvailability(flavorAvailability) {
		Object.keys(flavorAvailability).forEach(flavor => {
			const status = flavorAvailability[flavor];
			const flavorElements = document.querySelectorAll(`[data-flavor="${flavor}"]`);

			flavorElements.forEach(flavorElement => {
				if (status === 'sold_out') {
					flavorElement.classList.add('sold-out');
					flavorElement.classList.add('unavailable');
					const input = flavorElement.querySelector('input[type="radio"]');
					if (input) input.disabled = true;
				} else {
					flavorElement.classList.remove('sold-out');
					flavorElement.classList.remove('unavailable');
					const input = flavorElement.querySelector('input[type="radio"]');
					if (input) input.disabled = false;
				}
			});
		});
	}

	// Function to parse new flavor tags
	parseFlavorNewTags(newTagsString) {
		if (!newTagsString) return [];

		return newTagsString.split(',').map(flavor => flavor.trim());
	}

	// Function to update new flavor tags display
	updateFlavorNewTags(newFlavors) {
		// First, remove all new tags
		document.querySelectorAll('.flavor-option.has-new-tag').forEach(element => {
			element.classList.remove('has-new-tag');
		});

		// Then add new tags to specified flavors
		newFlavors.forEach(flavor => {
			const flavorElements = document.querySelectorAll(`[data-flavor="${flavor}"]`);
			flavorElements.forEach(flavorElement => {
				flavorElement.classList.add('has-new-tag');
			});
		});
	}

	calculateStockPercentage() {
		// Find all stock period start elements on the page
		const startDateElements = document.querySelectorAll('#stock-period-start')

		startDateElements.forEach(startDateElement => {
			// Find the end date element within the same container
			const percentContainer = startDateElement.closest('.remaining-percent-block')
			if (!percentContainer) return

			const endDateElement = percentContainer.querySelector('#stock-period-end')
			if (!endDateElement) return

			const startDateStr = startDateElement.dataset.date
			const endDateStr = endDateElement.dataset.date

			if (!startDateStr || !endDateStr) return

			const startDate = new Date(startDateStr)
			const endDate = new Date(endDateStr)
			const today = new Date()

			// Ensure dates are valid
			if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return

			// Calculate total duration and elapsed time
			const totalDuration = endDate.getTime() - startDate.getTime()
			const elapsedTime = today.getTime() - startDate.getTime()

			// Calculate percentage (how far we are from start to end date)
			let percentage = Math.round((elapsedTime / totalDuration) * 100)

			// Clamp percentage between 0 and 100
			percentage = Math.max(0, Math.min(100, percentage))

			// Calculate remaining percentage (inverse for progress bar)
			const remainingPercent = 100 - percentage

			// Find the remaining-block and sold-through-percent elements within the container
			const remainingBlock = percentContainer.querySelector('.remaining-block')
			const soldThroughElement = percentContainer.querySelector('#sold-through-percent')

			// Update the progress bar
			if (remainingBlock) {
				remainingBlock.style.flexBasis = `${remainingPercent}%`
			}

			// Update the sold through percentage text
			if (soldThroughElement) {
				soldThroughElement.textContent = `${percentage}% Sold Through`
			}
		})
	}

	rebuild() {

		try {
			// Clean up existing event listeners BEFORE reinitializing
			this.cleanup()

			// Reinitialize the component
			this.elements = this.getElements()
			this.init()

		} catch (error) {
			console.error('❌ Error rebuilding ProductFormV2 component:', error)
		}
	}

	cleanup() {

		// Remove add to cart button listener using stored handler
		const addToCartButton = this.form?.querySelector('.add-to-cart-button')
		if (addToCartButton && this.addToCartHandler) {
			addToCartButton.removeEventListener('click', this.addToCartHandler)
		}

		// Remove ALL event listeners by cloning and replacing the button
		// This ensures no duplicate listeners remain
		if (addToCartButton) {
			const newButton = addToCartButton.cloneNode(true)
			addToCartButton.parentNode.replaceChild(newButton, addToCartButton)
		}

		// Don't clear productData and currentSelections - we need them for rebuilding
		// Only clear references to DOM elements and handlers
		this.elements = null
		this.pendingSelectionRestore = null
		this.addToCartHandler = null

	}

	// Check if selected variant is disabled and auto-select next available variant
	checkAndAutoSelectAvailableVariant() {
		
		if (!this.currentSelections || !this.elements) {
			return
		}

		const { purchaseType } = this.currentSelections
		let selectedOption = null
		let isDisabled = false

		if (purchaseType === 'subscribe') {
			// Check subscription plans
			const checkedPlan = this.form.querySelector('.subscription-plans:not(.hidden) input[name="selling_plan_ui"]:checked')
			if (checkedPlan) {
				selectedOption = checkedPlan
				const planOption = checkedPlan.closest('.plan-option')
				// Check if disabled via data-available attribute or radio disabled state
				isDisabled = planOption?.dataset.available === 'false' || checkedPlan.disabled || planOption?.classList.contains('hidden')
			}
		} else {
			// Check one-time variants
			const checkedVariant = this.form.querySelector('.onetime-options:not(.hidden) input[name="variant_id"]:checked')
			if (checkedVariant) {
				selectedOption = checkedVariant
				const variantOption = checkedVariant.closest('.variant-option')
				// Check if disabled via data-available attribute or radio disabled state
				isDisabled = variantOption?.dataset.available === 'false' || checkedVariant.disabled || variantOption?.classList.contains('hidden')
			}
		}

		// If selected option is disabled, find and select next available option
		if (selectedOption && isDisabled) {
			
			let nextAvailableOption = null

			if (purchaseType === 'subscribe') {
				// Find next available subscription plan
				const allPlanRadios = Array.from(
					this.elements.subscriptionPlans.querySelectorAll(
						'input[type="radio"][name="selling_plan_ui"]'
					)
				).filter(radio => {
					const planOption = radio.closest('.plan-option')
					return !planOption?.classList.contains('hidden') && 
						   !radio.disabled && 
						   planOption?.dataset.available !== 'false'
				})

				// Find the first available option (or next one after current if possible)
				nextAvailableOption = allPlanRadios.find(radio => {
					const planOption = radio.closest('.plan-option')
					return planOption?.dataset.available === 'true' && !radio.disabled
				}) || allPlanRadios[0]

				if (nextAvailableOption) {
					
					// Uncheck current option
					selectedOption.checked = false
					selectedOption.removeAttribute('checked')
					selectedOption.closest('.plan-option')?.classList.remove('selected')
					
					// Check new option
					nextAvailableOption.checked = true
					nextAvailableOption.setAttribute('checked', 'checked')
					const newPlanOption = nextAvailableOption.closest('.plan-option')
					if (newPlanOption) {
						newPlanOption.classList.add('selected')
					}
					
					// Update selections
					const resolvedPlanId = nextAvailableOption.value.toString().split("_")[0]
					if (this.elements.globalSellingPlanSelect) {
						this.elements.globalSellingPlanSelect.value = resolvedPlanId
					}
					const planVariantId = nextAvailableOption.dataset.variantId
					if (planVariantId) {
						this.updateVariant(planVariantId)
					}
					this.updateSubscriptionPlan(resolvedPlanId)
					
					// Update month supply from the new selection
					const newMonthSupply = nextAvailableOption.getAttribute('option2')
					if (newMonthSupply) {
						this.currentSelections.monthSupply = newMonthSupply
						if (this.lastMonthSupplyByType) {
							this.lastMonthSupplyByType.subscribe = newMonthSupply
						}
					}
					
					// Update pricing and form
					this.updatePricing()
					this.updateFormFields()
					this.updateAddToCartButton()
				}
			} else {
				// Find next available one-time variant
				const allVariantRadios = Array.from(
					this.elements.onetimeOptions.querySelectorAll(
						'input[type="radio"][name="variant_id"]'
					)
				).filter(radio => {
					const variantOption = radio.closest('.variant-option')
					return !variantOption?.classList.contains('hidden') && 
						   !radio.disabled && 
						   variantOption?.dataset.available !== 'false'
				})

				// Find the first available option (or next one after current if possible)
				nextAvailableOption = allVariantRadios.find(radio => {
					const variantOption = radio.closest('.variant-option')
					return variantOption?.dataset.available === 'true' && !radio.disabled
				}) || allVariantRadios[0]

				if (nextAvailableOption) {
					
					// Uncheck current option
					selectedOption.checked = false
					selectedOption.removeAttribute('checked')
					selectedOption.closest('.variant-option')?.classList.remove('selected')
					
					// Check new option
					nextAvailableOption.checked = true
					nextAvailableOption.setAttribute('checked', 'checked')
					const newVariantOption = nextAvailableOption.closest('.variant-option')
					if (newVariantOption) {
						newVariantOption.classList.add('selected')
					}
					
					// Update selections
					this.updateVariant(nextAvailableOption.value)
					
					// Update month supply from the new selection
					const newMonthSupply = newVariantOption?.dataset?.monthSupply
					if (newMonthSupply) {
						this.currentSelections.monthSupply = newMonthSupply
						if (this.lastMonthSupplyByType) {
							this.lastMonthSupplyByType.onetime = newMonthSupply
						}
					}
					
					// Update pricing and form
					this.updatePricing()
					this.updateFormFields()
					this.updateAddToCartButton()
				}
			}

			if (!nextAvailableOption) {
			}
		} else if (selectedOption && !isDisabled) {
		} else {
		}
	}
}

const productFormV2 = new ProductFormV2()

// Calculate and update stock percentage on page load
document.addEventListener('DOMContentLoaded', () => {
	productFormV2.calculateStockPercentage()
})
